import { useEffect, useRef, useState } from "react";
import {
  getCategoryMeta,
  fetchStockAcrossSites,
} from "./ItemMasterService";

const STOCK_PAGE_SIZE = 20;

/**
 * Right-hand slide-in preview for an Item Master row.
 * "Stock across sites" is mock data until the backend exposes an
 * item-stock endpoint (see ItemMasterService).
 */
export default function ItemPreviewDrawer({
  item,
  onClose,
  onEdit,
  onToggleStatus,
}) {
  const [stock, setStock] = useState({
    rows: [],
    summary: null,
    total: 0,
    pageNumber: 1,
    search: "",
  });
  const searchTimeout = useRef(null);
  const isOpen = !!item;
  const meta = getCategoryMeta(item?.category || "general");
  const isSupply = item?.category === "supply";
  const isService = item?.category === "service";

  const loadStock = (pageNumber, search, append) => {
    fetchStockAcrossSites(item, {
      search,
      pageNumber,
      pageSize: STOCK_PAGE_SIZE,
    }).then((res) => {
      setStock((pre) => ({
        rows: append ? [...pre.rows, ...res.stock] : res.stock,
        summary: res.summary,
        total: res.pageInfo.total,
        pageNumber,
        search,
      }));
    });
  };

  useEffect(() => {
    if (isOpen && isSupply) {
      loadStock(1, "", false);
    } else {
      setStock({ rows: [], summary: null, total: 0, pageNumber: 1, search: "" });
    }
  }, [item?.id, isOpen]);

  const handleStockSearch = (e) => {
    const { value } = e.target;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      loadStock(1, value, false);
    }, 400);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const InfoRow = ({ label, value }) => (
    <div className="erp-drawer-info-row">
      <span className="label">{label}</span>
      <span className="value">{value ?? "--"}</span>
    </div>
  );

  return (
    <>
      <div className="erp-drawer-backdrop" onClick={onClose} />
      <div className="erp-drawer open">
        <div className="erp-drawer-header">
          <h4 className="rajdhani-semibold mb-0">Item Preview</h4>
          <button type="button" className="btn-close" onClick={onClose} />
        </div>
        <div className="erp-drawer-body">
          <div className="d-flex gap-3 align-items-center mb-4">
            {item?.photoUrl ? (
              <img
                src={item?.photoUrl}
                alt={item?.name}
                className="erp-item-photo-lg"
              />
            ) : (
              <div
                className={`erp-item-photo-fallback erp-item-photo-lg ${item?.category || "general"}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                  <path d="m3.3 7 8.7 5 8.7-5" />
                  <path d="M12 22V12" />
                </svg>
              </div>
            )}
            <div>
              <h5 className="rajdhani-semibold mb-1">{item?.name}</h5>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <span
                  className={`badge ${meta.badgeClass} fw-normal rounded-pill text-capitalize`}
                >
                  {meta.label}
                </span>
                <span
                  className={`badge ${item?.status ? "bg-green-25" : "bg-red-25"} fw-normal rounded-pill`}
                >
                  {item?.status ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>

          <div className="erp-drawer-section">
            <h6>Basic Info</h6>
            <InfoRow label="Item Code" value={item?.code} />
            <InfoRow label={meta.groupLabel} value={item?.expenseHead} />
            <InfoRow label="Unit (UOM)" value={item?.unit} />
            <InfoRow
              label="Standard Rate"
              value={item?.rateDisplay || item?.standardRate || "--"}
            />
            {item?.description && (
              <InfoRow label="Description" value={item?.description} />
            )}
          </div>

          <div className="erp-drawer-section">
            <h6>Tax Info</h6>
            <InfoRow label={meta.taxCodeLabel} value={item?.hsnCode} />
            <InfoRow label="GST %" value={item?.gstDisplay || "--"} />
            {isService && (
              <InfoRow label="TDS Section" value={item?.tdsSection || "--"} />
            )}
          </div>

          {isSupply && (
            <div className="erp-drawer-section">
              <h6>Supply Details</h6>
              <InfoRow label="Make" value={item?.make || "--"} />
              <InfoRow label="Model" value={item?.model || "--"} />
              <InfoRow label="Stock Type" value={item?.itemType} />
              <InfoRow label="Min Stock" value={item?.minStock || "--"} />
              <InfoRow
                label="Serialized"
                value={item?.isSerialized ? "Yes" : "No"}
              />
              <InfoRow
                label="Warranty"
                value={
                  item?.warrantyMonths
                    ? `${item?.warrantyMonths} months`
                    : "--"
                }
              />
            </div>
          )}

          {isService && (
            <div className="erp-drawer-section">
              <h6>Service Details</h6>
              <InfoRow label="Rate Basis" value={item?.rateBasis || "--"} />
            </div>
          )}

          {isSupply && (
            <div className="erp-drawer-section">
              <h6>Stock Across Sites</h6>
              {stock.summary && (
                <div className="erp-stock-cards mb-3">
                  <div className="erp-stock-card">
                    <div className="qty">
                      {stock.summary.totalQuantity} {item?.unit}
                    </div>
                    <div className="site">Total Stock</div>
                  </div>
                  <div className="erp-stock-card">
                    <div className="qty">{stock.summary.sitesWithStock}</div>
                    <div className="site">
                      of {stock.summary.totalSites} sites hold stock
                    </div>
                  </div>
                </div>
              )}
              <input
                className="form-control form-control-sm mb-2"
                placeholder="Search site..."
                onChange={handleStockSearch}
              />
              <div className="erp-stock-list">
                {stock.rows.map((row) => (
                  <div className="erp-stock-row" key={row.siteId}>
                    <span className="site">{row.siteName}</span>
                    <span className="qty">
                      {row.quantity} {row.unit}
                    </span>
                  </div>
                ))}
                {stock.rows.length === 0 && (
                  <p className="text-muted text-center mb-0 py-3">
                    No sites found
                  </p>
                )}
              </div>
              {stock.rows.length < stock.total && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary w-100 mt-2"
                  onClick={() =>
                    loadStock(stock.pageNumber + 1, stock.search, true)
                  }
                >
                  Load more ({stock.rows.length} of {stock.total})
                </button>
              )}
            </div>
          )}
        </div>
        <div className="erp-drawer-footer">
          <button
            type="button"
            className="btn btn-primary flex-fill"
            onClick={() => onEdit(item)}
          >
            Edit
          </button>
          <button
            type="button"
            className={`btn flex-fill ${item?.status ? "btn-outline-danger" : "btn-outline-success"}`}
            onClick={() => onToggleStatus(item, item?.status)}
          >
            {item?.status ? "Deactivate" : "Activate"}
          </button>
        </div>
      </div>
    </>
  );
}
